import { ReactElement } from 'react'
import { buildErrorMessage } from '@constants'
import { useIsOrganisationAdmin } from '@hooks'
import Delete from '@mui/icons-material/Delete'
import StayPrimaryLandscapeIcon from '@mui/icons-material/StayPrimaryLandscape'
import StayPrimaryPortraitIcon from '@mui/icons-material/StayPrimaryPortrait'
import Upload from '@mui/icons-material/Upload'
import { LoadingButton } from '@mui/lab'
import { Chip, IconButton, TableCell, TableRow, Tooltip } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import DownLoadIconButton from '@ui/actions/DownLoadIconButton'
import { useSnackbar } from '@ui/snackbar'
import { orientationLabels } from '../../constants'
import downloadCustomerFile from '../../downloadCustomerFile'
import { PlanTemplateConfiguration, planTemplateConfigurationKey } from '../../utils'

interface Props {
  configuration: PlanTemplateConfiguration
  customTemplate?: CustomerFile<PlanTemplateAdditionalData>
  onDownloadDefault: () => void
  isDownloadingDefault: boolean
  onUpload: (file: File) => void
  isUploading: boolean
  onDelete: () => void
  isDeleting: boolean
}

const PlanTemplateTableRow = ({
  configuration,
  customTemplate,
  onDownloadDefault,
  isDownloadingDefault,
  onUpload,
  isUploading,
  onDelete,
  isDeleting,
}: Props): ReactElement => {
  const configurationKey = planTemplateConfigurationKey(configuration)
  // A member who does not administer the organisation may download the
  // templates but not replace them.
  const { isOrganisationAdmin } = useIsOrganisationAdmin()
  const readOnly = !isOrganisationAdmin
  const { enqueueSnackbar } = useSnackbar()

  const { mutate: downloadCustomTemplate, isLoading: isDownloadingCustom } = useMutation(
    downloadCustomerFile,
    {
      onError: (error: Error) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Herunterladen der eigenen Vorlage'), {
          variant: 'error',
        })
      },
    },
  )

  return (
    <TableRow data-cy={`plan-template-row-${configurationKey}`}>
      <TableCell>{configuration.paperFormat}</TableCell>
      <TableCell align="center">
        <Tooltip title={orientationLabels[configuration.orientation]} enterDelay={1000}>
          {configuration.orientation === 'landscape' ? (
            <StayPrimaryLandscapeIcon fontSize="small" />
          ) : (
            <StayPrimaryPortraitIcon fontSize="small" />
          )}
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        {customTemplate ? (
          <Chip label="Angepasst" color="primary" size="small" />
        ) : (
          <Chip label="Standard" size="small" />
        )}
      </TableCell>
      <TableCell align="center">
        <Tooltip title="Standardvorlage herunterladen">
          <span>
            <DownLoadIconButton
              size="small"
              sx={{ color: 'action.active' }}
              loading={isDownloadingDefault}
              onClick={onDownloadDefault}
              data-cy={`download-default-template-${configurationKey}`}
            />
          </span>
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        {customTemplate?.url ? (
          <Tooltip title="Eigene Vorlage herunterladen">
            <span>
              <DownLoadIconButton
                size="small"
                sx={{ color: 'action.active' }}
                loading={isDownloadingCustom}
                onClick={() => downloadCustomTemplate(customTemplate)}
                data-cy={`download-custom-template-${configurationKey}`}
              />
            </span>
          </Tooltip>
        ) : (
          '–'
        )}
      </TableCell>
      <TableCell align="center">
        {!readOnly && (
          <LoadingButton
            component="label"
            size="small"
            startIcon={<Upload fontSize="small" />}
            loading={isUploading}
            data-cy={`upload-custom-template-${configurationKey}`}
          >
            Hochladen
            <input
              hidden
              type="file"
              accept="application/pdf"
              onChange={event => {
                const file = event.target.files?.[0]
                if (file) onUpload(file)
                event.target.value = ''
              }}
            />
          </LoadingButton>
        )}
      </TableCell>
      <TableCell align="center">
        {customTemplate && !readOnly && (
          <Tooltip title="Eigene Vorlage entfernen">
            <span>
              <IconButton
                size="small"
                onClick={onDelete}
                disabled={isDeleting}
                data-cy={`delete-custom-template-${configurationKey}`}
              >
                <Delete fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  )
}

export default PlanTemplateTableRow
